import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { getErrorMessage } from '@/utils/errorMessage';
import {
	invalidateSessionVideoRoomQueries,
	invalidateSessionsQueries,
	prependSessionToMeSessionsCache,
	RECONCILE_VIDEO_ROOM_SUCCESS_MESSAGES,
	reconcileSessionVideoRoom,
	upsertSessionInSessionsCache,
} from '@/utils/sessionQueryCache';
import { SessionService } from '../session.service';

export const usePaySession = (
	onSuccess?: (data: Sessions.PaySessionResponse) => void,
) => {
	const queryClient = useQueryClient();

	const paySessionMutation = useMutation({
		mutationFn: ({
			id,
			gateway,
			user_club_subscription_benefit_id,
		}: {
			id: string;
			gateway: Sessions.PaymentGateway;
			user_club_subscription_benefit_id?: string;
		}) =>
			SessionService.pay(id, gateway, user_club_subscription_benefit_id),
		onSuccess: (data) => {
			queryClient.invalidateQueries({ queryKey: ['sessions'] });
			if (onSuccess) onSuccess(data);
		},
		onError: (error: unknown) => {
			toast.error(
				getErrorMessage(error, 'Erro ao processar o pagamento'),
			);
		},
	});

	return paySessionMutation;
};

export const useCreateSession = (
	onSuccess?: (session: Sessions.Session) => void,
) => {
	const queryClient = useQueryClient();

	const createSessionMutation = useMutation({
		mutationFn: (body: Sessions.Create) => SessionService.create(body),
		onSuccess: async (session) => {
			prependSessionToMeSessionsCache(queryClient, session);
			await queryClient.invalidateQueries({
				queryKey: ['sessions'],
				exact: false,
			});
			await queryClient.invalidateQueries({
				queryKey: ['available-dates'],
			});

			if (onSuccess) onSuccess(session);
		},
		onError: (error: unknown) => {
			const msg = getErrorMessage(error, '');
			// Cadastro + agendamento: o componente trata re-pick; evita toast genérico em cima do fluxo guiado.
			if (/passado|antecedência|antecedencia/i.test(msg)) return;
			toast.error(getErrorMessage(error, 'Erro na criação da consulta'));
		},
	});

	return createSessionMutation;
};

// export const usePatchSessionPaymentStatus = (onSuccess?: () => void) => {
//   const queryClient = useQueryClient();

//   const patchSessionPaymentMutation = useMutation({
//     mutationFn: (data: Sessions.PatchPayment) => SessionService.patchPayment(data),
//     onSuccess: () => {
//       queryClient.invalidateQueries({ queryKey: ["sessions"] });
//       toast.success('Pagamento atualizado para concluido com sucesso');
//       if (onSuccess) onSuccess();
//     },
//     onError: (error: AxiosError) => {
//       if (typeof error === 'object' && error !== null && 'response' in error) {
//         const axiosError = error as { response?: { data?: { message?: string } } };
//         toast.error(axiosError.response?.data?.message || 'Erro na atualização do pagamento');
//       } else {
//         toast.error('Erro na atualização do pagamento');
//       }
//     },
//   });

//   return patchSessionPaymentMutation;
// };

export const usePatchSessionDate = (
	onSuccess?: (session: Sessions.Session) => void,
) => {
	const queryClient = useQueryClient();

	const patchSessionDateMutation = useMutation({
		mutationFn: (data: Sessions.PatchDate) =>
			SessionService.patchDate(data),
		onSuccess: async (updatedSession) => {
			upsertSessionInSessionsCache(queryClient, updatedSession);
			await invalidateSessionsQueries(queryClient);
			await queryClient.invalidateQueries({
				queryKey: ['available-dates'],
			});

			toast.success('Agendamento atualizado com sucesso');

			if (updatedSession.is_online) {
				await invalidateSessionVideoRoomQueries(
					queryClient,
					updatedSession.id,
				);
			}

			if (onSuccess) onSuccess(updatedSession);
		},
		onError: (error: unknown) => {
			toast.error(
				getErrorMessage(
					error,
					'Erro na atualização do agendamento da sessão',
				),
			);
		},
	});

	return patchSessionDateMutation;
};

export const usePatchSessionModality = (onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	const patchSessionModalityMutation = useMutation({
		mutationFn: (data: Sessions.PatchModality) =>
			SessionService.patchModality(data),
		onSuccess: async (updatedSession) => {
			upsertSessionInSessionsCache(queryClient, updatedSession);
			await invalidateSessionsQueries(queryClient);
			toast.success('Modalidade da consulta atualizada com sucesso');
			if (onSuccess) onSuccess();
		},
		onError: (error: unknown) => {
			toast.error(
				getErrorMessage(
					error,
					'Erro na atualização da modalidade da sessão',
				),
			);
		},
	});

	return patchSessionModalityMutation;
};

export const useCancelProfessionalSession = (onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (payload: { id: string; reason: string }) =>
			SessionService.cancelAsProfessional(payload),
		onSuccess: async () => {
			await invalidateSessionsQueries(queryClient);
			await queryClient.invalidateQueries({
				queryKey: ['available-dates'],
			});
			toast.success('Consulta cancelada com sucesso');
			onSuccess?.();
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao cancelar a consulta'));
		},
	});
};

export const useCancelUserSession = (
	onSuccess?: (result: {
		credit_restored: boolean;
		within_policy: boolean;
		session: Sessions.Session;
	}) => void,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (payload: { id: string; reason: string }) =>
			SessionService.cancelAsUser(payload),
		onSuccess: async (result) => {
			await invalidateSessionsQueries(queryClient);
			await queryClient.invalidateQueries({
				queryKey: ['available-dates'],
			});
			await queryClient.invalidateQueries({ queryKey: ['clubs'] });
			await queryClient.invalidateQueries({
				queryKey: ['user-club-subscription'],
			});
			toast.success(
				result.credit_restored
					? 'Consulta cancelada. Seu crédito foi devolvido.'
					: 'Consulta cancelada.',
			);
			onSuccess?.(result);
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao cancelar a consulta'));
		},
	});
};

export const usePatchSessionStatus = (onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	const patchSessionStatusMutation = useMutation({
		mutationFn: (data: Sessions.PatchStatus) =>
			SessionService.patchStatus(data),
		onSuccess: async (updatedSession) => {
			upsertSessionInSessionsCache(queryClient, updatedSession);
			await invalidateSessionsQueries(queryClient);
			toast.success('Status da consulta atualizada com sucesso');
			if (onSuccess) onSuccess();
		},
		onError: (error: unknown) => {
			toast.error(
				getErrorMessage(
					error,
					'Erro na atualização dos status da sessão',
				),
			);
		},
	});

	return patchSessionStatusMutation;
};

export const usePatchSessionMetadata = (onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	const patchSessionMetadataMutation = useMutation({
		mutationFn: (data: Sessions.PatchMetadata) =>
			SessionService.patchMetadata(data),
		onSuccess: async (updatedSession) => {
			upsertSessionInSessionsCache(queryClient, updatedSession);
			await invalidateSessionsQueries(queryClient);
			toast.success('Tipo da consulta atualizado com sucesso');
			if (onSuccess) onSuccess();
		},
		onError: (error: unknown) => {
			toast.error(
				getErrorMessage(
					error,
					'Erro na atualização do tipo da consulta',
				),
			);
		},
	});

	return patchSessionMetadataMutation;
};

export const usePatchSessionDisplayName = (onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	const patchSessionDisplayNameMutation = useMutation({
		mutationFn: (data: Sessions.PatchDisplayName) =>
			SessionService.patchDisplayName(data),
		onSuccess: async (updatedSession) => {
			upsertSessionInSessionsCache(queryClient, updatedSession);
			await invalidateSessionsQueries(queryClient);
			toast.success('Nome da sessão atualizado com sucesso');
			if (onSuccess) onSuccess();
		},
		onError: (error: unknown) => {
			toast.error(
				getErrorMessage(error, 'Erro na atualização do nome da sessão'),
			);
		},
	});

	return patchSessionDisplayNameMutation;
};

export const usePatchSessionStart = (onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	const patchSessionStartMutation = useMutation({
		mutationFn: (id: string) => SessionService.patchStart(id),
		onSuccess: async (updatedSession) => {
			upsertSessionInSessionsCache(queryClient, updatedSession);
			await invalidateSessionsQueries(queryClient);
			toast.success('Alterado status para iniciado com sucesso');
			if (onSuccess) onSuccess();
		},
		onError: (error: unknown) => {
			toast.error(
				getErrorMessage(
					error,
					'Erro na atualização dos status da sessão',
				),
			);
		},
	});

	return patchSessionStartMutation;
};

export const usePatchSessionEnd = (onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	const patchSessionEndMutation = useMutation({
		mutationFn: (input: Sessions.PatchSessionEndInput) => {
			if (typeof input === 'string') {
				return SessionService.patchEnd(input);
			}

			const { id, raw_notes } = input;
			return SessionService.patchEnd(
				id,
				raw_notes?.trim() ? { raw_notes: raw_notes.trim() } : undefined,
			);
		},
		onSuccess: async (updatedSession) => {
			upsertSessionInSessionsCache(queryClient, updatedSession);
			await invalidateSessionsQueries(queryClient);
			toast.success('Alterado status para finalizado com sucesso');
			if (onSuccess) onSuccess();
		},
		onError: (error: unknown) => {
			toast.error(
				getErrorMessage(
					error,
					'Erro na atualização dos status da sessão',
				),
			);
		},
	});

	return patchSessionEndMutation;
};

export const useReconcileSessionVideoRoom = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => reconcileSessionVideoRoom(queryClient, id),
		onSuccess: (data) => {
			toast.success(RECONCILE_VIDEO_ROOM_SUCCESS_MESSAGES[data.status]);
		},
		onError: (error: unknown) => {
			toast.error(
				getErrorMessage(
					error,
					'Erro ao sincronizar a sala de vídeo da sessão',
				),
			);
		},
	});
};

export const usePatchSessionParticipants = (onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	const patchSessionParticipantsMutation = useMutation({
		mutationFn: (payload: Sessions.PatchParticipants) =>
			SessionService.patchParticipants(payload),
		onSuccess: async (updatedSession, variables) => {
			upsertSessionInSessionsCache(queryClient, updatedSession);
			await invalidateSessionsQueries(queryClient);
			await queryClient.invalidateQueries({
				queryKey: ['session', variables.id],
			});
			toast.success('Participantes da sessão atualizados');
			if (onSuccess) onSuccess();
		},
		onError: (error: unknown) => {
			toast.error(
				getErrorMessage(
					error,
					'Erro ao atualizar participantes da sessão',
				),
			);
		},
	});

	return patchSessionParticipantsMutation;
};
